import { FC, ReactNode, useState } from 'react';
import { useBlocker, useNavigate } from '@tanstack/react-router';
import { useGetCurrentUserQuery, useGetEvaluationQuery } from '@teachur/http-client';
import type { DegreePlan } from '@teachur/http-client';
import { ConfirmationModal } from '@new/common/ui/ConfirmationModal/ConfirmationModal';
import { usePlanForm } from '../../hooks/usePlanForm';
import { preselectedDegreeId } from '../../lib/degreeChoice';
import { DASHBOARD_ROUTE } from '../../lib/routes';
import { BuildPlanIntroStep } from '../BuildPlanIntroStep/BuildPlanIntroStep';
import { ChooseDegreeStep } from '../ChooseDegreeStep/ChooseDegreeStep';
import { DegreeChoiceModal } from '../DegreeChoiceModal/DegreeChoiceModal';
import { PlanBuilder } from '../PlanBuilder/PlanBuilder';
import { PlanReviewHeader } from '../PlanReviewHeader/PlanReviewHeader';
import { Plans } from '../Plans/Plans';
import { PlanFooterBar } from '../PlanFooterBar/PlanFooterBar';
import { TranscriptSummaryCard } from '../TranscriptSummaryCard/TranscriptSummaryCard';

type WizardPhase = 'intro' | 'choose' | 'building' | 'review';

const LEAVE_TITLE = 'Leave without saving?';
const LEAVE_DESCRIPTION =
  "You have unsaved changes to your plan. If you leave now, those changes won't be saved.";
const REGENERATE_TITLE = 'Regenerate your plan?';
const REGENERATE_DESCRIPTION =
  "This rebuilds your plan from your latest transfer credits and evaluation. Any course changes you haven't saved will be discarded.";

interface CourseBuilderWizardProps {
  plans: DegreePlan[];
}

export const CourseBuilderWizard: FC<CourseBuilderWizardProps> = ({ plans: initialPlans }) => {
  const hasSavedPlan = initialPlans.length > 0;
  const [phase, setPhase] = useState<WizardPhase>(() => (hasSavedPlan ? 'review' : 'intro'));
  const [confirmingRegenerate, setConfirmingRegenerate] = useState(false);
  const [choosingDegree, setChoosingDegree] = useState(false);
  const [buildDegreeId, setBuildDegreeId] = useState<number | null>(null);
  const form = usePlanForm(initialPlans);
  const saveDisabled =
    form.plans.length === 0 || (!form.isDirty && form.plans.every((p) => p.saved));
  const navigate = useNavigate();
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: evaluation } = useGetEvaluationQuery();

  const blocker = useBlocker({
    shouldBlockFn: form.hasUnsavedChanges,
    enableBeforeUnload: form.hasUnsavedChanges,
    withResolver: true,
  });

  const onChooseDegree = () => setPhase('choose');

  const onGenerate = () => {
    if (form.isDirty) {
      setConfirmingRegenerate(true);
      return;
    }
    setChoosingDegree(true);
  };
  const onConfirmRegenerate = () => {
    setConfirmingRegenerate(false);
    setChoosingDegree(true);
  };
  const onCancelRegenerate = () => setConfirmingRegenerate(false);

  const onDegreeChosen = (degreeId: number) => {
    setChoosingDegree(false);
    setBuildDegreeId(degreeId);
    setPhase('building');
  };
  const onCancelDegreeChoice = () => setChoosingDegree(false);

  const onPlanBuilt = (plans: DegreePlan[]) => {
    form.applySuggestion(plans);
    setPhase('review');
  };
  const onCancelBuild = () => {
    setPhase(hasSavedPlan || form.hasUnsavedChanges() ? 'review' : 'choose');
  };

  const onSave = async () => {
    if (await form.save()) {
      navigate({ to: DASHBOARD_ROUTE });
    }
  };

  const onLeave = () => blocker.proceed?.();
  const onStay = () => blocker.reset?.();

  const currentDegreeId =
    form.plans[form.plans.length - 1]?.degree.id ??
    preselectedDegreeId(initialPlans, currentUser, evaluation?.targetDegreeId);

  let content: ReactNode;
  if (phase === 'intro') {
    content = (
      <div className="p-4 sm:p-6">
        <BuildPlanIntroStep onChooseDegree={onChooseDegree} />
      </div>
    );
  } else if (phase === 'choose') {
    content = (
      <div className="p-4 sm:p-6">
        <ChooseDegreeStep initialDegreeId={currentDegreeId} onGenerate={onDegreeChosen} />
      </div>
    );
  } else if (phase === 'building' && buildDegreeId != null) {
    content = (
      <div className="p-4 sm:p-6">
        <PlanBuilder degreeId={buildDegreeId} onPlanBuilt={onPlanBuilt} onCancel={onCancelBuild} />
      </div>
    );
  } else {
    content = (
      <div className="flex flex-col gap-4 p-4 sm:p-6 lg:p-8">
        <PlanReviewHeader regenerateDisabled={form.isSubmitting} onRegenerate={onGenerate} />
        <TranscriptSummaryCard />
        <Plans plans={form.plans} onChange={form.setPlans} />
        <PlanFooterBar onSave={onSave} saving={form.isSubmitting} disabled={saveDisabled} />
      </div>
    );
  }

  return (
    <>
      {content}
      {choosingDegree && (
        <DegreeChoiceModal
          isOpen
          initialDegreeId={currentDegreeId}
          onClose={onCancelDegreeChoice}
          onConfirm={onDegreeChosen}
        />
      )}
      <ConfirmationModal
        isOpen={blocker.status === 'blocked'}
        title={LEAVE_TITLE}
        description={LEAVE_DESCRIPTION}
        confirmText="Leave"
        cancelText="Stay"
        onConfirm={onLeave}
        onClose={onStay}
      />
      <ConfirmationModal
        isOpen={confirmingRegenerate}
        title={REGENERATE_TITLE}
        description={REGENERATE_DESCRIPTION}
        confirmText="Regenerate"
        cancelText="Cancel"
        onConfirm={onConfirmRegenerate}
        onClose={onCancelRegenerate}
      />
    </>
  );
};
